<template>
  <Modal :show="showForm" title="New Collection Request" size="lg" @close="handleClose">
    <form @submit.prevent class="space-y-5">
      <!-- Supplier Selection -->
      <div>
        <label for="cr-supplier" class="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
          Supplier <span class="text-red-500" aria-hidden="true">*</span><span class="sr-only">(required)</span>
        </label>
        <select
          id="cr-supplier"
          v-model="selectedSupplierId"
          required
          :disabled="!!prefill"
          class="w-full px-4 py-2.5 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
        >
          <option :value="null" disabled>{{ suppliersLoading ? 'Loading suppliers…' : 'Select a supplier…' }}</option>
          <option
            v-for="supplier in suppliers"
            :key="supplier.id"
            :value="supplier.id"
          >
            {{ supplier.suppliername }}
          </option>
        </select>
        <div v-if="suppliersError" class="mt-2 flex items-center gap-2 text-xs text-red-600 dark:text-red-400">
          <span>{{ suppliersError }}</span>
          <button type="button" @click="fetchSuppliers" class="font-medium underline hover:no-underline">Retry</button>
        </div>
      </div>

      <!-- Show additional fields after supplier selection -->
      <div v-if="selectedSupplier" class="space-y-5">
        <!-- Location Selection (if available) -->
        <div v-if="locations && locations.length">
          <label for="cr-location" class="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
            Location <span class="text-red-500" aria-hidden="true">*</span><span class="sr-only">(required)</span>
          </label>
          <select
            id="cr-location"
            v-model="selectedLocationId"
            required
            :disabled="!!prefill"
            class="w-full px-4 py-2.5 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
          >
            <option :value="null" disabled>Select a location…</option>
            <option
              v-for="location in locations"
              :key="location.locationid"
              :value="location.locationid"
            >
              {{ location.locationname }}
            </option>
          </select>
        </div>

        <!-- Odoo Link Warning — shown after location is selected (or immediately for no-location suppliers) -->
        <div v-if="odooStatusReady && !isCurrentSelectionLinked" class="flex items-start gap-3 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/40 rounded-lg">
          <span class="material-icons text-amber-500 text-lg flex-shrink-0 mt-0.5" aria-hidden="true">warning</span>
          <div>
            <p class="text-sm font-medium text-amber-800 dark:text-amber-300">Not linked to Odoo</p>
            <p class="text-xs text-amber-600 dark:text-amber-400 mt-0.5">
              A Purchase Order will not be created automatically when this collection is completed.
              Link the supplier in <strong>Odoo Sync</strong> before scheduling.
            </p>
          </div>
        </div>

        <!-- Odoo Linked Confirmation -->
        <div v-if="odooStatusReady && isCurrentSelectionLinked" class="flex items-center gap-2 p-2.5 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800/40 rounded-lg">
          <span class="material-icons text-emerald-500 text-sm" aria-hidden="true">check_circle</span>
          <p class="text-xs font-medium text-emerald-700 dark:text-emerald-300">Odoo linked — PO will be created on collection</p>
        </div>

        <!-- Request Date -->
        <div>
          <label for="cr-date" class="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
            Request Date <span class="text-red-500" aria-hidden="true">*</span><span class="sr-only">(required)</span>
          </label>
          <select
            id="cr-date"
            v-model="requestDate"
            required
            class="w-full px-4 py-2.5 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
          >
            <option :value="null" disabled>{{ datesLoading ? 'Loading dates…' : 'Select a date…' }}</option>
            <option
              v-for="date in dateList"
              :key="date.timestamp"
              :value="date.timestamp"
            >
              {{ date.date }}
            </option>
          </select>
          <div v-if="datesError" class="mt-2 flex items-center gap-2 text-xs text-red-600 dark:text-red-400">
            <span>{{ datesError }}</span>
            <button type="button" @click="retryFetchDates" class="font-medium underline hover:no-underline">Retry</button>
          </div>
        </div>

        <!-- Additional Requirements -->
        <div>
          <p class="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-3">
            Additional Requirements
          </p>
          <div class="space-y-2">
            <label class="flex items-center space-x-3 cursor-pointer">
              <input
                type="checkbox"
                v-model="replaceContainer"
                class="w-4 h-4 text-primary-600 bg-white dark:bg-neutral-700 border-neutral-300 dark:border-neutral-600 rounded focus:ring-primary-500 focus:ring-2"
              />
              <span class="text-sm text-neutral-700 dark:text-neutral-300">Replace Container</span>
            </label>
            <label class="flex items-center space-x-3 cursor-pointer">
              <input
                type="checkbox"
                v-model="highPriority"
                class="w-4 h-4 text-primary-600 bg-white dark:bg-neutral-700 border-neutral-300 dark:border-neutral-600 rounded focus:ring-primary-500 focus:ring-2"
              />
              <span class="text-sm text-neutral-700 dark:text-neutral-300">High Priority</span>
            </label>
            <label class="flex items-center space-x-3 cursor-pointer">
              <input
                type="checkbox"
                v-model="additionalContainer"
                class="w-4 h-4 text-primary-600 bg-white dark:bg-neutral-700 border-neutral-300 dark:border-neutral-600 rounded focus:ring-primary-500 focus:ring-2"
              />
              <span class="text-sm text-neutral-700 dark:text-neutral-300">Additional Container</span>
            </label>
          </div>
        </div>

        <!-- Estimated Volume (display only) -->
        <div>
          <label for="cr-volume" class="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
            Estimated Volume
          </label>
          <input
            id="cr-volume"
            type="text"
            :value="estimatedVolume"
            disabled
            class="w-full px-4 py-2.5 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-neutral-50 dark:bg-neutral-900 text-neutral-500 dark:text-neutral-400 cursor-not-allowed"
          />
        </div>
      </div>

      <!-- Create failure banner -->
      <div v-if="errorMessage" role="alert" class="flex items-start gap-3 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/40 rounded-lg">
        <span class="material-icons text-red-500 text-lg flex-shrink-0 mt-0.5" aria-hidden="true">error</span>
        <div>
          <p class="text-sm font-medium text-red-800 dark:text-red-300">Could not create collection request</p>
          <p class="text-xs text-red-700 dark:text-red-400 mt-0.5">{{ errorMessage }}</p>
        </div>
      </div>
    </form>

    <template #footer>
      <div class="flex items-center justify-end gap-3">
        <button
          @click="handleClose"
          :disabled="isPending"
          type="button"
          class="px-5 py-2.5 text-sm font-medium text-neutral-700 dark:text-neutral-300 bg-white dark:bg-neutral-700 border border-neutral-300 dark:border-neutral-600 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-600 transition-colors disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          @click="handleClick"
          :disabled="isPending || !canSubmit"
          type="button"
          class="px-5 py-2.5 text-sm font-medium text-white bg-primary-700 rounded-lg hover:bg-primary-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          <svg
            v-if="isPending"
            class="animate-spin h-4 w-4"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <span>Create</span>
        </button>
      </div>
    </template>
  </Modal>
</template>

<script>
import axios from "axios";
import { datePart, dayLabel, dayValue } from "@/utils/dubaiDay";
import { ref, watch, computed, onMounted, nextTick } from "vue";
import { useStore } from "vuex";
import Modal from "@/components/ui/Modal.vue";
import useToast from "@/composables/useToast";

export default {
  components: { Modal },
  props: { showForm: Boolean, prefill: { type: Object, default: null } },
  emits: ["close", "created"],
  setup(props, { emit }) {
    const store = useStore();
    const apiUrl = store.state.apiUrl;
    const siteId = store.state.currentSite;
    const { showToast } = useToast();

    // Reactive variables for form data
    const suppliers = ref([]);
    const selectedSupplierId = ref(null);
    const selectedSupplier = ref(null);
    const locations = ref([]);
    const selectedLocationId = ref(null);
    const requestDate = ref(null);
    const dateList = ref([]);
    const replaceContainer = ref(false);
    const highPriority = ref(false);
    const additionalContainer = ref(false);
    // Used when a supplier/location has no containers on record, so the field is
    // never left blank for the planner to guess at.
    const DEFAULT_ESTIMATED_VOLUME = 150;
    const estimatedVolume = ref(null);
    const isPending = ref(false);
    const geotag = ref(null);
    const errorMessage = ref("");
    const suppliersLoading = ref(false);
    const suppliersError = ref("");
    const datesLoading = ref(false);
    const datesError = ref("");

    // Odoo link check — supplier-level for no-location suppliers, location-level otherwise
    const odooStatusReady = computed(() => {
      if (!selectedSupplier.value) return false;
      const locs = locations.value || [];
      // For suppliers with locations, wait until a location is selected
      if (locs.length > 0) return !!selectedLocationId.value;
      return true;
    });

    const isCurrentSelectionLinked = computed(() => {
      if (!selectedSupplier.value) return false;
      const locs = locations.value || [];
      if (locs.length > 0 && selectedLocationId.value) {
        // Check location-level odooId
        const loc = locs.find((l) => l.locationid === selectedLocationId.value);
        return !!(loc && loc.odooId);
      }
      // No locations — check supplier-level odooId
      return !!selectedSupplier.value.odooId;
    });

    // All required fields chosen? (location only when the supplier has locations)
    const canSubmit = computed(() => {
      if (!selectedSupplierId.value || !requestDate.value) return false;
      if (locations.value && locations.value.length && !selectedLocationId.value) return false;
      return true;
    });

    // Fetch suppliers via API
    const fetchSuppliers = async () => {
      suppliersLoading.value = true;
      suppliersError.value = "";
      try {
        const response = await axios.get(`${apiUrl}/suppliers/${siteId}`);
        suppliers.value = response.data;
      } catch (error) {
        console.error("Error fetching suppliers:", error);
        suppliersError.value = "Couldn't load suppliers.";
      } finally {
        suppliersLoading.value = false;
      }
    };

    onMounted(async () => {
      await fetchSuppliers();
      applyPrefill();
    });

    // Prefill from a suggestion (Suggestions page "Edit…"). Supplier first (its watcher loads
    // locations + dates), then location, then the date once the planner list arrives.
    const applyPrefill = () => {
      const p = props.prefill;
      if (!p) return;
      const sup = suppliers.value.find((s) => s.id === p.supplierId || s.supplierid === p.supplierId);
      if (!sup) return;
      selectedSupplierId.value = sup.id;
      nextTick(() => {
        if (p.locationId && String(p.locationId) !== "0") {
          selectedLocationId.value = parseInt(p.locationId, 10);
        }
        if (p.estimatedVolume) estimatedVolume.value = Math.round(p.estimatedVolume);
        const wanted = datePart(p.requestedDate);
        const stop = watch(dateList, (list) => {
          const hit = (list || []).find((d) => datePart(d.timestamp) === wanted);
          if (hit) requestDate.value = hit.timestamp;
          if (list && list.length) stop();
        });
      });
    };

    // When supplier selection changes, update selectedSupplier and locations
    watch(selectedSupplierId, () => {
      selectedSupplier.value = suppliers.value.find(
        (supplier) => supplier.id === selectedSupplierId.value
      );
      geotag.value = selectedSupplier.value.geotag;
      selectedLocationId.value = null;
      requestDate.value = null;
      locations.value = selectedSupplier.value
        ? selectedSupplier.value.locations
        : [];
      // If no locations are available, calculate estimated volume from supplier containers
      if (
        selectedSupplier.value &&
        (!locations.value || locations.value.length === 0)
      ) {
        let vol = 0;
        if (selectedSupplier.value.containers) {
          selectedSupplier.value.containers.forEach((container) => {
            vol += container.maxVolume * container.quantity * 0.8;
          });
        }
        if (!props.prefill || !props.prefill.estimatedVolume) {
          estimatedVolume.value = vol > 0 ? vol : DEFAULT_ESTIMATED_VOLUME;
        }
      } else {
        // Supplier HAS locations: clear the previous supplier's estimate so a stale
        // figure can't linger until a location is picked. The location watcher sets it.
        estimatedVolume.value = null;
      }
      // Fetch available dates (pass locationId=0 if none selected)
      if (selectedSupplierId.value) {
        const effectiveLocationId = selectedLocationId.value || 0;
        fetchDates(selectedSupplierId.value, effectiveLocationId);
      } else {
        dateList.value = [];
      }
    });

    // When location selection changes, update estimated volume and fetch dates
    watch(selectedLocationId, () => {
      if (locations.value && selectedLocationId.value) {
        const selectedLoc = locations.value.find(
          (loc) => loc.locationid === selectedLocationId.value
        );
        geotag.value = selectedLoc.areaname;
        // Estimate from the containers recorded at THIS location. Fall back to the
        // same DEFAULT_ESTIMATED_VOLUME the no-locations path uses when the location
        // has no containers on record (or the supplier has none at all) — otherwise
        // the field was left untouched and rendered blank.
        let vol = 0;
        if (selectedSupplier.value && selectedSupplier.value.containers) {
          selectedSupplier.value.containers.forEach((container) => {
            if (container.locationId == selectedLocationId.value) {
              vol += container.maxVolume * container.quantity * 0.8;
            }
          });
        }
        if (!props.prefill || !props.prefill.estimatedVolume) {
          estimatedVolume.value = vol > 0 ? vol : DEFAULT_ESTIMATED_VOLUME;
        }
        fetchDates(selectedSupplierId.value, selectedLocationId.value);
      }
    });

    // Function to fetch available dates via API
    const fetchDates = async (supplierId, locationId) => {
      datesLoading.value = true;
      datesError.value = "";
      try {
        const response = await axios.get(
          `${apiUrl}/getSupplierLocationPlannerData/${supplierId}/${locationId}`
        );
        // Assume the API returns an array of ISO date strings
        // Label and value must name the SAME calendar day for every browser
        // timezone: label from the day itself (UTC-rendered), value canonical.
        dateList.value = response.data.map((isoDate) => {
          const day = datePart(isoDate);
          return {
            date: dayLabel(day),
            timestamp: dayValue(day),
          };
        });
      } catch (err) {
        console.error("Error fetching dates:", err);
        datesError.value = "Couldn't load available dates.";
      } finally {
        datesLoading.value = false;
      }
    };

    const retryFetchDates = () => {
      if (!selectedSupplierId.value) return;
      fetchDates(selectedSupplierId.value, selectedLocationId.value || 0);
    };

    // Close modal by emitting event
    const closeModal = () => {
      emit("close");
    };

    // Ignore close requests (Cancel, X, Escape) while the create is in flight.
    const handleClose = () => {
      if (isPending.value) return;
      closeModal();
    };

    // Handle the create button click: post the new collection request via API
    const handleClick = async () => {
      if (!canSubmit.value) return;
      isPending.value = true;
      errorMessage.value = "";
      try {
        const collectionRequest = {
          supplierName: selectedSupplier.value.suppliername,
          supplierId: selectedSupplierId.value,
          locationId: selectedLocationId.value ? selectedLocationId.value : "0",
          locationName: selectedLocationId.value
            ? (
                locations.value.find(
                  (loc) => loc.locationid === selectedLocationId.value
                ) || {}
              ).locationname
            : null,
          createdAt: new Date().toISOString(),
          orderType: "COLLECTION",
          createdBy: store.state.user.displayName,
          siteId: selectedSupplier.value.siteid,
          countryid: selectedSupplier.value.countryid,
          geotag: geotag.value || null,
          requestedBy: "Neutral Fuels",
          status: "REQUESTED",
          modifiedAt: new Date().toISOString(),
          requestedDate: requestDate.value, // expect an ISO string from the select
          scheduledDate: null,
          replaceContainer: replaceContainer.value,
          highPriority: highPriority.value,
          additionalContainer: additionalContainer.value,
          estimatedVolume: estimatedVolume.value,
        };
        const response = await axios.post(`${apiUrl}/createCollectionRequest`, collectionRequest);
        showToast("success", "Collection request created");
        // Parent may need the id (Suggestions page links the suggestion to this request).
        emit("created", (response.data && response.data.id) || null);
      } catch (error) {
        console.error("Error creating collection request:", error);
        errorMessage.value =
          error.response?.data?.detail ||
          "Failed to create the collection request. Please try again.";
      } finally {
        isPending.value = false;
      }
    };

    return {
      suppliers,
      selectedSupplierId,
      selectedSupplier,
      locations,
      selectedLocationId,
      requestDate,
      dateList,
      replaceContainer,
      highPriority,
      additionalContainer,
      estimatedVolume,
      isPending,
      odooStatusReady,
      isCurrentSelectionLinked,
      canSubmit,
      errorMessage,
      suppliersLoading,
      suppliersError,
      datesLoading,
      datesError,
      fetchSuppliers,
      retryFetchDates,
      closeModal,
      handleClose,
      handleClick,
    };
  },
};
</script>
